import { Check, Clock, FileText, Send, XCircle, AlertCircle, DollarSign } from "lucide-react";
import { cn } from "@/lib/utils";

interface StatusStep {
  id: string;
  label: string;
  description: string;
  icon: React.ElementType;
  color: string;
  bgColor: string;
}

const statusSteps: StatusStep[] = [
  {
    id: "unbilled",
    label: "Unbilled",
    description: "Service completed, awaiting billing",
    icon: FileText,
    color: "text-muted-foreground",
    bgColor: "bg-muted",
  },
  {
    id: "pending",
    label: "Pending Review",
    description: "Claim under internal review",
    icon: Clock,
    color: "text-amber-600",
    bgColor: "bg-amber-100",
  },
  {
    id: "submitted",
    label: "Submitted",
    description: "Sent to payer for processing",
    icon: Send,
    color: "text-blue-600",
    bgColor: "bg-blue-100",
  },
  {
    id: "approved",
    label: "Paid",
    description: "Payment received",
    icon: DollarSign,
    color: "text-emerald-600",
    bgColor: "bg-emerald-100",
  },
];

interface ClaimStatusTimelineProps {
  currentStatus: string | null;
  isDenied?: boolean;
  className?: string;
}

export default function ClaimStatusTimeline({ 
  currentStatus, 
  isDenied = false,
  className 
}: ClaimStatusTimelineProps) {
  const normalizedStatus = currentStatus || "unbilled";
  
  // Find current step index
  const currentStepIndex = statusSteps.findIndex(step => step.id === normalizedStatus);
  const activeIndex = isDenied ? -1 : currentStepIndex;

  return (
    <div className={cn("space-y-3", className)}>
      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
        <AlertCircle className="h-4 w-4" />
        Claim Lifecycle
      </div>
      
      {/* Timeline */}
      <div className="relative">
        {/* Progress line */}
        <div className="absolute left-5 top-0 h-full w-0.5 bg-muted" />
        
        {/* Completed progress line */}
        {activeIndex >= 0 && (
          <div 
            className="absolute left-5 top-0 w-0.5 bg-emerald-500 transition-all duration-500"
            style={{ 
              height: `calc(${(activeIndex / (statusSteps.length - 1)) * 100}% + 10px)` 
            }}
          />
        )}

        <div className="relative space-y-4">
          {statusSteps.map((step, index) => {
            const isCompleted = !isDenied && index < activeIndex;
            const isCurrent = !isDenied && index === activeIndex;
            const isPending = !isDenied && index > activeIndex;
            const Icon = step.icon;

            return (
              <div
                key={step.id}
                className={cn(
                  "flex items-start gap-3 transition-opacity duration-300",
                  isPending && "opacity-50"
                )}
              >
                {/* Icon */}
                <div
                  className={cn(
                    "relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 transition-all duration-300",
                    isCompleted && "border-emerald-500 bg-emerald-500 text-white",
                    isCurrent && `border-current ${step.color} ${step.bgColor}`,
                    isPending && "border-muted bg-background text-muted-foreground"
                  )}
                >
                  {isCompleted ? (
                    <Check className="h-5 w-5" />
                  ) : (
                    <Icon className="h-5 w-5" />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 pt-1">
                  <div className="flex items-center gap-2">
                    <p
                      className={cn(
                        "font-medium text-sm",
                        isCompleted && "text-emerald-600",
                        isCurrent && step.color,
                        isPending && "text-muted-foreground"
                      )}
                    >
                      {step.label}
                    </p>
                    {isCurrent && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
                        Current
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {step.description}
                  </p>
                </div>
              </div>
            );
          })}

          {/* Denied state - shown as branch */}
          {isDenied && (
            <div className="flex items-start gap-3 ml-6 pl-6 border-l-2 border-red-300">
              <div className="relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 border-red-500 bg-red-100 text-red-600">
                <XCircle className="h-5 w-5" />
              </div>
              <div className="flex-1 pt-1">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-sm text-red-600">Denied</p>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">
                    Current
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Claim was denied by payer
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Status summary */}
      <div className={cn(
        "flex items-center gap-2 p-3 rounded-lg mt-4",
        isDenied ? "bg-red-50 text-red-700" : 
        normalizedStatus === "approved" ? "bg-emerald-50 text-emerald-700" :
        normalizedStatus === "submitted" ? "bg-blue-50 text-blue-700" :
        normalizedStatus === "pending" ? "bg-amber-50 text-amber-700" :
        "bg-muted text-muted-foreground"
      )}>
        {isDenied ? (
          <>
            <XCircle className="h-4 w-4" />
            <span className="text-sm font-medium">Claim denied - review for resubmission</span>
          </>
        ) : normalizedStatus === "approved" ? (
          <>
            <Check className="h-4 w-4" />
            <span className="text-sm font-medium">Payment received successfully</span>
          </>
        ) : normalizedStatus === "submitted" ? (
          <>
            <Clock className="h-4 w-4" />
            <span className="text-sm font-medium">Awaiting payer response</span>
          </>
        ) : normalizedStatus === "pending" ? (
          <>
            <Clock className="h-4 w-4" />
            <span className="text-sm font-medium">Ready to submit to payer</span>
          </>
        ) : (
          <>
            <FileText className="h-4 w-4" />
            <span className="text-sm font-medium">Start billing process</span>
          </>
        )}
      </div>
    </div>
  );
}
